import { useRequest } from "@certn/api";
import {
  Divider,
  MediaQuery,
  SecondaryTopNav,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  mq,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";

import { ConfigurationError } from "../../components/ConfigurationError";
import { LegacyCaseDetailsTitleCard } from "./components/LegacyCaseDetailsTitleCard";
import { LegacyCommentSection } from "./components/LegacyCommentSection";
import { CopyLinkButton } from "../../components/copy-link-button/CopyLinkButton";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { Previous } from "../../types";
import { LegacyReportTab } from "./tabs/LegacyReportTab";
import { LegacyDocumentsTab } from "./tabs/LegacyDocumentsTab";
import { LegacyCommentSectionTab } from "./tabs/LegacyCommentSectionTab";

export function LegacyCaseDetailsPage() {
  const { reportId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const url = useResolvedPath("").pathname;
  const currentTab = pathname.replace(url, "");
  const [previous] = useState<Previous>(
    state?.last || { href: "/cases/legacy" }
  );

  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-md"].maxWidth.toString()}px)`
  );
  const { hasPermission } = usePermissions();
  const request = useRequest();

  // State management for legacy case details
  const [legacyCaseDetails, setLegacyCaseDetails] = useState<any>(null);
  const [legacyCaseDetailsIsLoading, setLegacyCaseDetailsIsLoading] =
    useState(true);
  const [legacyCaseDetailsError, setLegacyCaseDetailsError] =
    useState<any>(null);

  useEffect(() => {
    if (!reportId) return;

    const fetchLegacyCase = async () => {
      try {
        setLegacyCaseDetailsIsLoading(true);
        const response = await request(
          `/api/internal/client-portal/legacy-case/${reportId}/`
        );
        setLegacyCaseDetails(response);
        setLegacyCaseDetailsError(null);
      } catch (error) {
        setLegacyCaseDetailsError(error);
        setLegacyCaseDetails(null);
      } finally {
        setLegacyCaseDetailsIsLoading(false);
      }
    };

    fetchLegacyCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  async function updateLegacyCaseDetails() {
    if (!reportId) return;

    try {
      const response = await request(
        `/api/internal/client-portal/legacy-case/${reportId}/`
      );
      setLegacyCaseDetails(response);
    } catch (error) {
      console.error("Error updating legacy case details:", error);
    }
  }

  const userHasPermission =
    hasPermission({
      action: "read",
      target: "case",
      scope: "all",
    }) ||
    hasPermission({
      action: "read",
      target: "case",
      scope: "my_groups",
    });

  if (!userHasPermission) {
    return (
      <ConfigurationError
        message={t(
          "Hmm, it looks like you do not have access to this case. You may need to ask the group admin to be added as a member for access."
        )}
      />
    );
  }

  if (legacyCaseDetailsError) {
    console.log(legacyCaseDetailsError);
    return (
      <ConfigurationError
        message={t(
          "Unable to load legacy case details. Please try again later."
        )}
      />
    );
  }

  const displayName =
    legacyCaseDetails?.applicant_first_name &&
    legacyCaseDetails?.applicant_last_name
      ? `${legacyCaseDetails.applicant_first_name} ${legacyCaseDetails.applicant_last_name}`
      : legacyCaseDetails?.applicant_email;

  return (
    <div className={styles.noScrollWrapper}>
      <SecondaryTopNav
        leftIcon="close"
        testID="legacy-case-details-secondary-top-nav"
        onleftIconOnPress={() => navigate(previous.href)}
        titleArea={
          !isMobile
            ? {
                subtitle: "ID: " + (legacyCaseDetails?.report_id || ""),
                title: displayName || "",
              }
            : { subtitle: "", title: "" }
        }
      >
        <CopyLinkButton url={location.href} />
      </SecondaryTopNav>
      <Divider />
      <div className={styles.pageContent}>
        <div className={styles.scrollSection}>
          {!legacyCaseDetailsIsLoading && legacyCaseDetails && (
            <LegacyCaseDetailsTitleCard
              caseId={legacyCaseDetails.id}
              reportId={legacyCaseDetails.report_id}
              applicantFirstName={legacyCaseDetails.applicant_first_name}
              applicantLastName={legacyCaseDetails.applicant_last_name}
              applicantEmail={legacyCaseDetails.applicant_email}
              groupName={legacyCaseDetails.customer_group?.name}
              ownerName={legacyCaseDetails.customer_account?.auth0_display_name}
              requestedByName={legacyCaseDetails.requested_by?.auth0_full_name}
              status={legacyCaseDetails.status}
              score={legacyCaseDetails.score}
              teamType={legacyCaseDetails.team_type}
              created={legacyCaseDetails.created}
              modified={legacyCaseDetails.modified}
            />
          )}
          <Tabs
            gap="space-0"
            selectedKey={currentTab}
            testID="legacy-case-details-tabs"
          >
            <TabList
              aria-label={t("Type of legacy case details")}
              className={styles.tabList}
            >
              <Tab href={url} testID="legacy-case-details-report-tab">
                {t("Report")}
              </Tab>
              <Tab
                id="/documents"
                href={`${url}/documents`}
                testID="legacy-case-details-documents-tab"
              >
                {t("Documents")}
              </Tab>
              {isMobile && (
                <Tab
                  id="/comments"
                  href={`${url}/comments`}
                  testID="legacy-case-details-comments-tab"
                >
                  {t("Comments")}
                </Tab>
              )}
            </TabList>
            <TabPanel id={currentTab} className={styles.tabPanel}>
              <Routes>
                <Route
                  path=""
                  element={<LegacyReportTab reportId={reportId} />}
                />
                <Route
                  path="/documents"
                  element={<LegacyDocumentsTab reportId={reportId} />}
                />
                <Route
                  path="/comments"
                  element={
                    <LegacyCommentSectionTab
                      comments={legacyCaseDetails?.comments ?? []}
                      mutate={updateLegacyCaseDetails}
                    />
                  }
                />
              </Routes>
            </TabPanel>
          </Tabs>
        </div>
        <MediaQuery sm md type="hide">
          <LegacyCommentSection
            comments={legacyCaseDetails?.comments ?? []}
            mutate={updateLegacyCaseDetails}
          />
        </MediaQuery>
      </div>
    </div>
  );
}

const styles = {
  noScrollWrapper: css({
    height: "100vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  }),
  pageContent: css({
    display: "flex",
    flexDirection: "row",
    flex: 1,
    maxHeight: "100%",
    overflow: "hidden",
  }),
  scrollSection: css({
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: "var(--surface-sunken)",
  }),
  tabList: css(
    {
      backgroundColor: "var(--surface-base)",
    },
    mq({
      paddingLeft: ["var(--space-4)", "var(--space-8)"],
      paddingRight: ["var(--space-4)", "var(--space-8)"],
    })
  ),
  tabPanel: css(
    mq({
      paddingTop: ["var(--space-3)", "var(--space-5)"],
      paddingBottom: ["var(--space-3)", "var(--space-5)"],
      paddingLeft: [0, "var(--space-8)"],
      paddingRight: [0, "var(--space-8)"],
    })
  ),
};
